import React, { useState, useEffect, useMemo, useRef } from 'react';
import { format, startOfWeek, endOfWeek, addWeeks, subWeeks, parseISO } from 'date-fns';
import { attendanceApi, timecardApi, employeeApi } from '../services/api';
import { AttendanceRecord, Employee, TimecardDay } from '../types';
import { useWeekStartDay } from '../hooks/useWeekStartDay';
import { useLocationContext } from '../components/Layout';
import { extractDatePart } from '../utils/datetime';
import { WeekNavigator } from '../components/WeekNavigator';
import { parseLocalDate } from '../utils/payrollPeriodLocal';
import {
  CheckCircle2,
  XCircle,
  RefreshCw,
  Play,
  Loader2,
  Calendar,
  AlertCircle,
} from 'lucide-react';

interface VerificationResult {
  date: string;
  attendanceId: number;
  attendanceIn: string;
  attendanceOut: string | null;
  timecardIn: string | null;
  timecardOut: string | null;
  match: boolean;
  message?: string;
}

function normalizeTime(s: string | null | undefined): string | null {
  if (!s) return null;
  try {
    const d = parseISO(s);
    return format(d, "yyyy-MM-dd'T'HH:mm:ss");
  } catch {
    return s;
  }
}

function getEffectiveClockIn(att: AttendanceRecord): string {
  return att.adjustedClockIn || att.clockInTime;
}

function getEffectiveClockOut(att: AttendanceRecord): string | null {
  if (att.adjustedClockOut) return att.adjustedClockOut;
  return att.clockOutTime || null;
}

const AttendanceTimecardVerifier: React.FC = () => {
  const weekStartsOn = useWeekStartDay();
  const { selectedLocationId } = useLocationContext();
  const [currentWeek, setCurrentWeek] = useState(new Date());
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<number | null>(null);
  const [attendance, setAttendance] = useState<AttendanceRecord[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [results, setResults] = useState<VerificationResult[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const selectAllCheckboxRef = useRef<HTMLInputElement>(null);

  const weekStart = useMemo(
    () => startOfWeek(currentWeek, { weekStartsOn }),
    [currentWeek, weekStartsOn]
  );
  const weekEnd = useMemo(
    () => endOfWeek(currentWeek, { weekStartsOn }),
    [currentWeek, weekStartsOn]
  );
  const weekStartStr = format(weekStart, 'yyyy-MM-dd');
  const weekEndStr = format(weekEnd, 'yyyy-MM-dd');

  useEffect(() => {
    const el = selectAllCheckboxRef.current;
    if (el) {
      el.indeterminate = selectedIds.size > 0 && selectedIds.size < attendance.length;
    }
  }, [selectedIds, attendance.length]);

  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const data = await employeeApi.getAll();
        setEmployees(data);
      } catch (e) {
        console.error('Error fetching employees:', e);
      }
    };
    fetchEmployees();
  }, []);

  const loadAttendance = async () => {
    if (!selectedEmployeeId) {
      setError('Please select an employee.');
      return;
    }
    setLoading(true);
    setError(null);
    setResults(null);
    try {
      const data = await attendanceApi.getAll({
        employeeId: selectedEmployeeId,
        startDate: weekStartStr,
        endDate: weekEndStr,
      });
      setAttendance(data);
      setSelectedIds(new Set(data.map((a) => a.id)));
    } catch (e: any) {
      setError(e.response?.data?.message || e.message || 'Failed to load attendance.');
      setAttendance([]);
    } finally {
      setLoading(false);
    }
  };

  const toggleSelect = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === attendance.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(attendance.map((a) => a.id)));
    }
  };

  const runVerification = async () => {
    if (!selectedEmployeeId) {
      setError('Please select an employee.');
      return;
    }
    const toVerify = attendance.filter((a) => selectedIds.has(a.id));
    if (toVerify.length === 0) {
      setError('Select at least one attendance record to verify.');
      return;
    }

    setVerifying(true);
    setError(null);
    setResults(null);
    try {
      await timecardApi.sync(selectedEmployeeId, weekStartStr, selectedLocationId ?? undefined);
      const timecard = await timecardApi.getByEmployeeAndWeek(
        selectedEmployeeId,
        weekStartStr,
        selectedLocationId ?? undefined
      );

      const verificationResults: VerificationResult[] = [];
      for (const att of toVerify) {
        const dateStr = extractDatePart(att.clockInTime);
        const effectiveIn = getEffectiveClockIn(att);
        const effectiveOut = getEffectiveClockOut(att);

        const day: TimecardDay | undefined = timecard?.days?.find((d) => d.date === dateStr);
        const timecardIn = day?.actualIn ?? null;
        const timecardOut = day?.actualOut ?? null;

        const inMatch = normalizeTime(timecardIn) === normalizeTime(effectiveIn);
        const outMatch = effectiveOut
          ? normalizeTime(timecardOut) === normalizeTime(effectiveOut)
          : !timecardOut || timecardOut === '';

        const match = inMatch && outMatch;
        let message: string | undefined;
        if (!day) message = 'No timecard day (no shift for this date)';
        else if (!match) {
          const parts: string[] = [];
          if (!inMatch) parts.push(`In: expected ${effectiveIn}, got ${timecardIn}`);
          if (!outMatch) parts.push(`Out: expected ${effectiveOut}, got ${timecardOut}`);
          message = parts.join('; ');
        }

        verificationResults.push({
          date: dateStr,
          attendanceId: att.id,
          attendanceIn: effectiveIn,
          attendanceOut: effectiveOut,
          timecardIn,
          timecardOut,
          match,
          message,
        });
      }
      setResults(verificationResults);
    } catch (e: any) {
      setError(e.response?.data?.message || e.message || 'Verification failed.');
    } finally {
      setVerifying(false);
    }
  };

  const formatTimeDisplay = (s: string | null | undefined) => {
    if (!s) return '—';
    try {
      const d = parseISO(s);
      return format(d, 'HH:mm');
    } catch {
      return s;
    }
  };

  const allPassed = results && results.length > 0 && results.every((r) => r.match);

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-neutral-900 flex items-center gap-2">
          <CheckCircle2 className="h-7 w-7 text-blue-600" />
          Attendance → Timecard Verifier
        </h1>
        <p className="text-neutral-600 mt-1">
          Select records, run verification, and compare attendance with timecard entries.
        </p>
      </div>

      {/* Controls */}
      <div className="bg-white rounded-lg border border-neutral-200 p-4 mb-6 space-y-4">
        <div className="flex flex-wrap gap-4 items-end">
          {/* Employee */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">Employee</label>
            <select
              value={selectedEmployeeId ?? ''}
              onChange={(e) => {
                setSelectedEmployeeId(e.target.value ? Number(e.target.value) : null);
                setAttendance([]);
                setResults(null);
              }}
              className="border border-neutral-300 rounded-md px-3 py-2 min-w-[200px] focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Select employee</option>
              {employees.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.firstName} {e.lastName}
                </option>
              ))}
            </select>
          </div>

          {/* Week */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">Week</label>
            <WeekNavigator
              weekStart={weekStart}
              weekEnd={weekEnd}
              weekStartsOn={weekStartsOn}
              currentWeek={currentWeek}
              onPreviousWeek={() => setCurrentWeek(subWeeks(currentWeek, 1))}
              onNextWeek={() => setCurrentWeek(addWeeks(currentWeek, 1))}
              onCurrentWeek={() => setCurrentWeek(new Date())}
              onWeekSelect={(ymd) => setCurrentWeek(parseLocalDate(ymd))}
              showCurrentWeekButton={false}
            />
          </div>

          <button
            onClick={loadAttendance}
            disabled={loading || !selectedEmployeeId}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
            Load Attendance
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
          <AlertCircle className="h-5 w-5 flex-shrink-0" />
          {error}
        </div>
      )}

      {/* Attendance table with selection */}
      {attendance.length > 0 && (
        <div className="bg-white rounded-lg border border-neutral-200 overflow-hidden mb-6">
          <div className="px-4 py-3 border-b border-neutral-200 bg-neutral-50 flex items-center justify-between">
            <span className="text-sm font-medium text-neutral-700">
              {attendance.length} attendance record(s) – select records to verify
            </span>
            <button
              onClick={toggleSelectAll}
              className="text-sm text-blue-600 hover:text-blue-700"
            >
              {selectedIds.size === attendance.length ? 'Deselect all' : 'Select all'}
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-neutral-100 border-b border-neutral-200">
                  <th className="text-left px-4 py-2 w-10">
                    <input
                      ref={selectAllCheckboxRef}
                      type="checkbox"
                      checked={selectedIds.size === attendance.length && attendance.length > 0}
                      onChange={toggleSelectAll}
                      className="rounded"
                    />
                  </th>
                  <th className="text-left px-4 py-2">Date</th>
                  <th className="text-left px-4 py-2">Clock In</th>
                  <th className="text-left px-4 py-2">Clock Out</th>
                  <th className="text-left px-4 py-2">Source</th>
                </tr>
              </thead>
              <tbody>
                {attendance.map((att) => (
                  <tr
                    key={att.id}
                    className={`border-b border-neutral-100 hover:bg-neutral-50 ${
                      selectedIds.has(att.id) ? 'bg-blue-50/50' : ''
                    }`}
                  >
                    <td className="px-4 py-2">
                      <input
                        type="checkbox"
                        checked={selectedIds.has(att.id)}
                        onChange={() => toggleSelect(att.id)}
                        className="rounded"
                      />
                    </td>
                    <td className="px-4 py-2">
                      {extractDatePart(att.clockInTime)
                        ? format(parseISO(`${extractDatePart(att.clockInTime)}T12:00:00`), 'EEE d MMM')
                        : '—'}
                    </td>
                    <td className="px-4 py-2">{formatTimeDisplay(getEffectiveClockIn(att))}</td>
                    <td className="px-4 py-2">{formatTimeDisplay(getEffectiveClockOut(att))}</td>
                    <td className="px-4 py-2 text-neutral-500">
                      {att.clockInSource || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="px-4 py-3 border-t border-neutral-200 bg-neutral-50">
            <button
              onClick={runVerification}
              disabled={verifying || selectedIds.size === 0}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {verifying ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Play className="h-4 w-4" />
              )}
              Run Verification ({selectedIds.size} selected)
            </button>
          </div>
        </div>
      )}

      {/* Results */}
      {results && (
        <div className="bg-white rounded-lg border border-neutral-200 overflow-hidden">
          <div
            className={`px-4 py-3 border-b flex items-center gap-2 ${
              allPassed ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'
            }`}
          >
            {allPassed ? (
              <>
                <CheckCircle2 className="h-5 w-5 text-green-600" />
                <span className="font-medium text-green-800">All selected records match timecard.</span>
              </>
            ) : (
              <>
                <XCircle className="h-5 w-5 text-amber-600" />
                <span className="font-medium text-amber-800">
                  {results.filter((r) => !r.match).length} mismatch(es) found.
                </span>
              </>
            )}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-neutral-100 border-b border-neutral-200">
                  <th className="text-left px-4 py-2">Status</th>
                  <th className="text-left px-4 py-2">Date</th>
                  <th className="text-left px-4 py-2">Attendance In</th>
                  <th className="text-left px-4 py-2">Attendance Out</th>
                  <th className="text-left px-4 py-2">Timecard In</th>
                  <th className="text-left px-4 py-2">Timecard Out</th>
                  <th className="text-left px-4 py-2">Notes</th>
                </tr>
              </thead>
              <tbody>
                {results.map((r) => (
                  <tr
                    key={r.attendanceId}
                    className={`border-b border-neutral-100 ${
                      r.match ? 'bg-green-50/30' : 'bg-red-50/30'
                    }`}
                  >
                    <td className="px-4 py-2">
                      {r.match ? (
                        <CheckCircle2 className="h-5 w-5 text-green-600" />
                      ) : (
                        <XCircle className="h-5 w-5 text-red-600" />
                      )}
                    </td>
                    <td className="px-4 py-2">{r.date}</td>
                    <td className="px-4 py-2">{formatTimeDisplay(r.attendanceIn)}</td>
                    <td className="px-4 py-2">{formatTimeDisplay(r.attendanceOut)}</td>
                    <td className="px-4 py-2">{formatTimeDisplay(r.timecardIn)}</td>
                    <td className="px-4 py-2">{formatTimeDisplay(r.timecardOut)}</td>
                    <td className="px-4 py-2 text-neutral-600 text-xs max-w-[200px]">
                      {r.message || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!attendance.length && selectedEmployeeId && !loading && (
        <div className="bg-neutral-50 rounded-lg border border-neutral-200 p-8 text-center text-neutral-600">
          <Calendar className="h-12 w-12 mx-auto mb-2 text-neutral-400" />
          <p>No attendance records for this week.</p>
          <p className="text-sm mt-1">Load attendance for another week or employee.</p>
        </div>
      )}
    </div>
  );
};

export default AttendanceTimecardVerifier;
